"use client";
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import type { Shadows } from "@mui/material/styles";
import { ReactNode, useMemo } from "react";

const baseDesignTokens = {
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: "Inter, Roboto, system-ui, sans-serif",
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600 },
  } as const,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16 },
      },
    },
  },
};

// Locked single palette (dark style) requested by user: global background #252525
const getPalette = () => ({
  mode: "dark" as const,
  primary: { main: "#0867d2", contrastText: "#ffffff" },
  secondary: { main: "#f9c33c", contrastText: "#1e1e1e" }, // keeping legacy accent naming
  background: { default: "#252525", paper: "#2d2d2d" },
  text: { primary: "#f5f7fa", secondary: "#b5bdc7" },
  divider: "#3a3f45",
});

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const theme = useMemo(() => {
    const darkShadows: Shadows = [
      "none", // 0
      "0px 2px 4px rgba(0,0,0,0.15)", // 1
      "none", // 2
      "none", // 3
      "none", // 4
      "none", // 5
      "none", // 6
      "none", // 7
      "none", // 8
      "none", // 9
      "none", // 10
      "none", // 11
      "none", // 12
      "none", // 13
      "none", // 14
      "none", // 15
      "none", // 16
      "none", // 17
      "none", // 18
      "none", // 19
      "none", // 20
      "none", // 21
      "none", // 22
      "none", // 23
      "none", // 24
    ];
    return createTheme({
      palette: getPalette(),
      ...baseDesignTokens,
      // Always dark-style shadows since single palette is dark.
      shadows: darkShadows,
      typography: {
        ...baseDesignTokens.typography,
        button: {
          ...baseDesignTokens.typography.button,
          textTransform: "none",
        },
      },
      spacing: 8,
      components: {
        ...baseDesignTokens.components,
        MuiButton: {
          styleOverrides: {
            root: {
              paddingTop: 10,
              paddingBottom: 10,
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              paddingTop: 12,
              paddingBottom: 12,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: "#2d2d2d",
              backgroundImage: "none",
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            colorPrimary: {
              backgroundColor: "#2d2d2d",
              backgroundImage: "none",
              boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: "#1f1f1f",
              backgroundImage: "none",
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: "#2e2e2e",
              border: "1px solid #3a3f45",
            },
          },
        },
      },
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
